import { fromLonLat as olFromLonLat, toLonLat as olToLonLat } from 'ol/proj';
import { toStringHDMS } from 'ol/coordinate';
import { COORDINATE_FORMAT } from './constantes';

const PortAllData = {};

/**
 * Convert a distance to be compatible with a WMS server
 * @param {number} latitude Distance latitude
 * @param {number} distance Distance in meters, should be little regarding distance between two latitude lines (111 km)
 * @param {string} projection Name of the projection
 * @return {number} Distance after conversion
 */
export const circleRadiusToWMS = (latitude, distance, projection = 'EPSG:4326') => {
  switch (projection) {
    case 'EPSG:4326':
    // radius * cos( φ * π / 180 )
      return distance * Math.cos(latitude * Math.PI / 180);
    default:
      throw new Error(`Unknown projection: ${projection}`);
  }
};

/**
 * Convert a distance from a WMS server feature
 * @param {number} latitude Distance latitude
 * @param {number} distance Distance in meters, should be little regarding distance between two latitude lines (111 km)
 * @param {string} projection Name of the projection
 * @return {number} Distance after conversion
 */
export const circleRadiusFromWMS = (latitude, distance, projection = 'EPSG:4326') => {
  switch (projection) {
    case 'EPSG:4326':
    // radius / cos( φ * π / 180 )
      return distance / Math.cos(latitude * Math.PI / 180);
    default:
      throw new Error(`Unknown projection: ${projection}`);
  }
};

/**
 * Transforms a coordinate from longitude/latitude to the current projection.
 * @param {import('ol/coordinate').Coordinate} coordinate Coordinate as longitude and latitude, i.e.
 *     an array with longitude as 1st and latitude as 2nd element.
 * @return {import('ol/coordinate').Coordinate} Coordinate projected to the target projection.
 */
export function fromLonLat(coordinate) {
  return olFromLonLat(coordinate, 'EPSG:3857');
}

/**
 * Transforms a coordinate to longitude/latitude.
 * @param {import('ol/coordinate').Coordinate} coordinate Projected coordinate.
 * @return {import('ol/coordinate').Coordinate} Coordinate as longitude and latitude, i.e. an array
 *     with longitude as 1st and latitude as 2nd element.
 */
export function toLonLat(coordinate) {
  return olToLonLat(coordinate, 'EPSG:3857');
}

/**
 * @typedef {Object} ToStringCoordOptions
 * @property {number} dmdDec The DMD decimal number
 * @property {boolean} [asArray] true to return coord under array. Default: false
 */

/**
 * Get the string representation a geographic coordinate.
 * @param {import('ol/coordinate').Coordinate} coordinate The coordinate to tranform
 * @param {ToStringCoordOptions} options The toString options.
 * @return {string|Array<string>} The coordinates as to string.
 */
export function toStringCoordinates(coordinate, options = {}) {
  const format = PortAllData.coordinateFormat;
  let coordString;
  switch (format) {
    case COORDINATE_FORMAT.DEGREE_MINUTE_SECOND:
      coordString = toStringHDMS(coordinate);
      if (options.asArray) {
        const index = (coordString.includes('N') ? coordString.indexOf('N') : coordString.indexOf('S'));
        const latitude = coordString.slice(0, index + 1);
        const longitude = coordString.slice(index + 2);
        coordString = [longitude, latitude];
      }
      break;
    case COORDINATE_FORMAT.DEGREE_MINUTE_DECIMAL:
    default:
      coordString = toStringHDM(coordinate, options.dmdDec, options.asArray);
  }
  return coordString;
}

/**
 * Fonction -> https://openlayers.org/en/latest/apidoc/module-ol_coordinate.html#.toStringHDMS
 * Code -> https://github.com/openlayers/openlayers/blob/v6.6.1/src/ol/coordinate.js
 * @param {import('ol/coordinate').Coordinate} coordinate projected coordinates
 * @param {number} opt_fractionDigits
 * @param {boolean} asArray Return coordinate avec string array. Default: false
 * @returns {string|Array<string>} Coordinates as HDM string
 */
function toStringHDM(coordinate, opt_fractionDigits = 6, asArray = false) {
  let ret;
  if (coordinate) {
    const coord = [
      degreesToStringHDM('EW', coordinate[0], opt_fractionDigits),
      degreesToStringHDM('NS', coordinate[1], opt_fractionDigits),
    ];
    if (asArray) {
      ret = coord;
    } else {
      ret = `${coord[1]} ${coord[0]}`;
    }
  } else {
    ret = '';
  }
  return ret;
}

function degreesToStringHDM(hemispheres, degrees, opt_fractionDigits) {
  const normalizedDegrees = ((degrees + 180) % 360) - 180;
  const x = Math.abs(3600 * normalizedDegrees);
  const dflPrecision = opt_fractionDigits || 0;
  const precision = 10 ** dflPrecision;

  let deg = Math.floor(x / 3600);
  let min = Math.floor((x - deg * 3600) / 60);
  let sec = x - deg * 3600 - min * 60;

  if (sec >= 60) {
    sec = 0;
    min += 1;
  }

  if (min >= 60) {
    min = 0;
    deg += 1;
  }

  min += (sec / 60);
  min = Math.ceil(min * precision) / precision;

  return (
    `${deg
    }° ${
      min
    }'${
      normalizedDegrees == 0
        ? ''
        : ` ${hemispheres.charAt(normalizedDegrees < 0 ? 1 : 0)}`}`
  );
}

/** The degree minute decimal regex */
const DMD_COORDINATE_REGEX = /^\s*\d{1,2}°\s*[1-5]?\d((\.|,)\d+)?'\s*(N|S)\s*\d{1,3}°\s*[1-5]?\d((\.|,)\d+)?'\s*(E|W)\s*$/g;
/** The degree minute second regex */
const DMS_COMPLET_COORDINATE_REGEX = /^\s*\d{1,2}°\s*[1-5]?\d'\s*[1-5]?\d((\.|,)\d+)?"\s*(N|S)\s*\d{1,3}°\s*[1-5]?\d'\s*[1-5]?\d((\.|,)\d+)?"\s*(E|W)\s*$/g;
/** The degree regex */
const DD_COMPLET_COORDINATE_REGEX = /^\s*-?\d{1,2}((\.|,)\d+)?(,|\s)*-?\d{1,3}((\.|,)\d+)?\s*$/g;
/** The degree minute decimal regex */
const DMD_ONE_COORDINATE_REGEX = /^\s*\d{1,3}°\s*[1-5]?\d((\.|,)\d+)?'\s*(E|W|S|N)\s*$/g;
/** The degree minute second regex */
const DMS_ONE_COORDINATE_REGEX = /^\s*\d{1,3}°\s*[1-5]?\d'\s*[1-5]?\d((\.|,)\d+)?"\s*(E|W|S|N)\s*$/g;
/** The degree regex */
const DD_ONE_COORDINATE_REGEX = /^\s*-?\d{1,3}((\.|,)\d+)?\s*$/g;

/** The number regex */
const NUMBER_REGEX = /-?\d+(\.\d+)?/g;

/**
 * Transform a string coordinate to coordinate.
 * @param {string|Array<string>} coordinate String coordinate or [lon,lat] string array.
 * @return {import("ol/coordinate").Coordinate} coordinates
 */
export function fromStringCoordinates(coordinate) {
  let coord;
  if (typeof coordinate === 'string') {
    const format = getCompletCoordinateFormat(coordinate);
    NUMBER_REGEX.lastIndex = 0;
    const matches = coordinate.replace(/,/g, '.').match(NUMBER_REGEX);
    const latFactor = coordinate.includes('S') ? -1 : 1;
    const lonFactor = coordinate.includes('W') ? -1 : 1;
    switch (format) {
      case COORDINATE_FORMAT.DEGREE_DECIMAL:
        coord = [getDegree(matches[1]), getDegree(matches[0])];
        break;
      case COORDINATE_FORMAT.DEGREE_MINUTE_DECIMAL:
        coord = [lonFactor * getDegree(matches[2], matches[3]), latFactor * getDegree(matches[0], matches[1])];
        break;
      case COORDINATE_FORMAT.DEGREE_MINUTE_SECOND:
        coord = [lonFactor * getDegree(matches[3], matches[4], matches[5]), latFactor * getDegree(matches[0], matches[1], matches[2])];
        break;
      default:
        coord = null;
        break;
    }
  } else {
    coord = [parseCoord(coordinate[0]), parseCoord(coordinate[1])];
  }
  return coord;
}

/**
 * Get the coordinate format.
 * @param {string} coordinates The coordiate has string
 * @return {COORDINATE_FORMAT} The coordinate format
 */
function getCompletCoordinateFormat(coordinates) {
  DMS_COMPLET_COORDINATE_REGEX.lastIndex = 0;
  if (DMS_COMPLET_COORDINATE_REGEX.test(coordinates)) {
    return COORDINATE_FORMAT.DEGREE_MINUTE_SECOND;
  }
  DMD_COORDINATE_REGEX.lastIndex = 0;
  if (DMD_COORDINATE_REGEX.test(coordinates)) {
    return COORDINATE_FORMAT.DEGREE_MINUTE_DECIMAL;
  }
  DD_COMPLET_COORDINATE_REGEX.lastIndex = 0;
  if (DD_COMPLET_COORDINATE_REGEX.test(coordinates)) {
    return COORDINATE_FORMAT.DEGREE_DECIMAL;
  }
}

function parseCoord(coordinate) {
  let coord;
  const format = getCoordinateFormat(coordinate);
  NUMBER_REGEX.lastIndex = 0;
  const matches = coordinate.replace(/,/g, '.').match(NUMBER_REGEX);
  const factor = coordinate.includes('S') || coordinate.includes('W') ? -1 : 1;
  switch (format) {
    case COORDINATE_FORMAT.DEGREE_DECIMAL:
      coord = getDegree(matches[0]);
      break;
    case COORDINATE_FORMAT.DEGREE_MINUTE_DECIMAL:
      coord = factor * getDegree(matches[0], matches[1]);
      break;
    case COORDINATE_FORMAT.DEGREE_MINUTE_SECOND:
      coord = factor * getDegree(matches[0], matches[1], matches[2]);
      break;
    default:
      coord = null;
      break;
  }
  return coord;
}

/**
 * Get the coordinate format for one coord.
 * @param {string} coordinate The coordiate has string
 * @return {COORDINATE_FORMAT} The coordinate format
 */
function getCoordinateFormat(coordinate) {
  DMS_ONE_COORDINATE_REGEX.lastIndex = 0;
  if (DMS_ONE_COORDINATE_REGEX.test(coordinate)) {
    return COORDINATE_FORMAT.DEGREE_MINUTE_SECOND;
  }
  DMD_ONE_COORDINATE_REGEX.lastIndex = 0;
  if (DMD_ONE_COORDINATE_REGEX.test(coordinate)) {
    return COORDINATE_FORMAT.DEGREE_MINUTE_DECIMAL;
  }
  DD_ONE_COORDINATE_REGEX.lastIndex = 0;
  if (DD_ONE_COORDINATE_REGEX.test(coordinate)) {
    return COORDINATE_FORMAT.DEGREE_DECIMAL;
  }
}

/**
 * Get de degree from deg, min sec
 * @param {string} degree The degree
 * @param {string} minutes The minute
 * @param {string} secondes The seconde
 * @returns {number} The degree decimal.
 */
function getDegree(degree, minutes = 0, secondes = 0) {
  return parseFloat(degree) + parseFloat(minutes) / 60 + parseFloat(secondes) / 3600;
}
